import { Loading, Tabs, TabsList, TabsTrigger } from "@repo/ui";
import { ReactNode, useEffect, useMemo, useState } from "react";

import { useRepSegmentsApi } from "../../api/repSegmentsApi";
import { useRepContext } from "../../contexts/editableItemContext";
import { useSegmentContext } from "../../contexts/segmentContext";
import { RepSegment } from "../../models";
import { RepStatusEnum } from "../../models/rep";
import { ReqIcon } from "../onboardingSessions/req";
import style from "./style.module.scss";

interface Props {
	children?: ReactNode[];
	renderSegment: (segment: RepSegment) => ReactNode;
}

const DEACTIVE_STATUSES = [RepStatusEnum.Deactivated, RepStatusEnum.OnHold, RepStatusEnum.Terminated];

export function OnboardingTabs(props: Props) {
	const { children, renderSegment } = props;
	const { repId, rep } = useRepContext();
	const { useRepSegmentsQuery } = useRepSegmentsApi(repId);
	const { segmentId } = useSegmentContext();
	const { data, loaded } = useRepSegmentsQuery();
	const [activeSegmentId, setActiveSegmentId] = useState(segmentId?.toString() ?? "");

	const segments = useMemo(() => {
		if (!data?.length) return data;
		if (!segmentId) return data;
		return data.filter((d) => d.id == segmentId);
	}, [data, segmentId]);
	const count = segments?.length ?? 0;

	const isRepDeactive = DEACTIVE_STATUSES.includes(rep.statusEnum);

	useEffect(() => {
		if (count > 0) {
			const firstId = segments[0].id;
			setActiveSegmentId(firstId.toString());
		}
	}, [segments]);

	return (
		<Loading loading={!loaded}>
			<Tabs value={activeSegmentId} onValueChange={setActiveSegmentId}>
				{count > 1 ? (
					<TabsList>
						{segments.map((segment) => (
							<TabsTrigger key={segment.id} value={segment.id.toString()} className={style.flexWithGap}>
								{segment.stateName}
								<ReqIcon
									completion={{
										name: segment.stateName + segment.statusId,
										required: true,
										done: !isRepDeactive && segment.statusId === RepStatusEnum.Active,
										contingentDone:
											!isRepDeactive &&
											(segment.statusId === RepStatusEnum.ContingentStart ||
												segment.statusId === RepStatusEnum.AwaitingContingentApproval),
										inProgress:
											!isRepDeactive &&
											(segment.statusId === RepStatusEnum.AwaitingContingentActivation ||
												segment.statusId === RepStatusEnum.Onboarding),
									}}
									small
								/>
							</TabsTrigger>
						))}
					</TabsList>
				) : null}

				{segments?.map((segment) => activeSegmentId === segment.id.toString() && renderSegment(segment))}
				{children}
			</Tabs>
		</Loading>
	);
}
